import { useState, useMemo, useEffect } from 'react';
import CommonModal from '../../common/Modal';
import { db } from '../../../firebase/config';
import {
    useFirestoreNoCompagny,
    useFirestoreNoCompagnyGeneralOrderBy,
    useFirestoreAllArrayContains,
    useFirestoreOrderBy,
} from '../../../firebase/useFirestore';
import { client } from '../../../hooks/Client';
import {
    subscribeToEffect,
    subscribeToQuestions,
} from '../../synchronisations/synchronisationActions';
import linkedSourceIds from '../../../helpers/synchronisations/linkedSourceIds';

// One-shot read of a portfolio effect's questions, used by the "whole effect"
// path (the drill-down step uses a useFirestoreAllArrayContains listener).
const fetchPortfolioQuestions = async (effectId, portfolioId) => {
    const snap = await db.collection('QuestionnaireFields')
        .where('EffectId', 'array-contains', effectId)
        .get();
    return snap.docs
        .map((doc) => ({ ...doc.data(), docid: doc.id }))
        .filter((q) => q.CompagnyID === portfolioId && !q.Deleted);
};

// Subscribe-side: a project picks effects (or specific questions) from a parent
// portfolio's catalog. Wizard flow: portfolio → effects list → optional
// drill-down for selecting questions + a target effect.
/**
 * @param {object} props
 * @param {boolean} props.modalOpen - Whether the modal is open
 * @param {(open: boolean) => void} props.setModalOpen - Setter to close the modal
 * @param {string} props.preselectedPortfolioId - CompagnyID of the portfolio whose effects are shown
 * @param {(added: Array<{id: string, docid: string}>) => void} [props.onSubscribed] - Called with the locally created effect(s) after linking the selected effects
 */
const SubscribePortfolioEffectsModal = ({ modalOpen, setModalOpen, preselectedPortfolioId, onSubscribed }) => {
    const [step, setStep] = useState('list'); // 'list' | 'questions'
    const [selectedPortfolioId, setSelectedPortfolioId] = useState('');
    const [pickedEffects, setPickedEffects] = useState(/** @type {Set<string>} */ (new Set()));
    const [drilldownEffectId, setDrilldownEffectId] = useState('');
    const [pickedQuestions, setPickedQuestions] = useState(new Set());
    const [targetEffectId, setTargetEffectId] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    // The chooser modal in Effects.jsx pre-picks the portfolio per option, so
    // we sync that down whenever it changes. Clear any effect selection too —
    // the picked IDs belong to the previously shown portfolio.
    useEffect(() => {
        if (preselectedPortfolioId) setSelectedPortfolioId(preselectedPortfolioId);
        setPickedEffects(new Set());
    }, [preselectedPortfolioId]);

    const allPortfolioMeta = useFirestoreNoCompagny('CompagnyMeta', 'CompagnyID', selectedPortfolioId || '__none__');
    const portfolioName = allPortfolioMeta?.[0]?.CommunityName || 'portfolio';

    // --- Data fetches ------------------------------------------------------

    const portfolioEffects = useFirestoreNoCompagnyGeneralOrderBy(
        'OutputEffects',
        'CompagnyID',
        selectedPortfolioId || '__none__',
        'Position',
        'asc'
    );
    const projectEffects = useFirestoreOrderBy('OutputEffects', 'Position', 'asc');
    const projectSyncs = useFirestoreNoCompagnyGeneralOrderBy(
        'Synchronisations',
        'Project',
        client,
        'Timestamp',
        'desc'
    );

    // Soft-deleted docs (Deleted: true, ADR-008) stay in Firestore but must
    // not be offered for linking.
    const portfolioEffectsList = useMemo(
        () => (Array.isArray(portfolioEffects) ? portfolioEffects : []).filter((e) => !e?.Deleted),
        [portfolioEffects]
    );

    // Effects this project already has an active link to are locked: picking
    // them again copied the effect and all its questions a second time (#291).
    // Until the links are loaded we can't tell, so everything stays locked.
    // AI-CONTEXT: See docs/pitfalls.md#state-gate-defaults-open-while-loading
    const syncsLoaded = Array.isArray(projectSyncs) && Array.isArray(projectEffects);
    const linkedEffectIds = useMemo(
        () => linkedSourceIds(projectSyncs, selectedPortfolioId, ['MSIEffect', 'Effect'], projectEffects),
        [projectSyncs, selectedPortfolioId, projectEffects]
    );
    const linkableEffects = useMemo(
        () => (syncsLoaded ? portfolioEffectsList.filter((e) => !linkedEffectIds.has(e.ID)) : []),
        [syncsLoaded, portfolioEffectsList, linkedEffectIds]
    );

    // MSI effects have standardised indicators — they can't be a target for
    // custom subscribed questions.
    const targetableProjectEffects = useMemo(
        () => (Array.isArray(projectEffects) ? projectEffects : []).filter(
            (e) => e?.Standard !== 'true' && !e?.Deleted
        ),
        [projectEffects]
    );

    const drilldownEffect = useMemo(
        () => portfolioEffectsList.find((e) => e.ID === drilldownEffectId) || null,
        [portfolioEffectsList, drilldownEffectId]
    );

    const drilldownQuestions = useFirestoreAllArrayContains(
        'QuestionnaireFields',
        'EffectId',
        drilldownEffectId || '__none__'
    );
    const drilldownPortfolioQuestions = useMemo(
        () => (Array.isArray(drilldownQuestions) ? drilldownQuestions : []).filter(
            (q) => q?.CompagnyID === selectedPortfolioId && !q?.Deleted
        ),
        [drilldownQuestions, selectedPortfolioId]
    );

    // --- Resets / close ----------------------------------------------------

    const resetDrilldown = () => {
        setStep('list');
        setDrilldownEffectId('');
        setPickedQuestions(new Set());
        setTargetEffectId('');
    };

    const closeModal = () => {
        resetDrilldown();
        setSelectedPortfolioId('');
        setPickedEffects(new Set());
        setBusy(false);
        setError('');
        setModalOpen(false);
    };

    // --- Actions -----------------------------------------------------------

    const togglePickedEffect = (effectId) => {
        setPickedEffects((prev) => {
            const next = new Set(prev);
            if (next.has(effectId)) next.delete(effectId);
            else next.add(effectId);
            return next;
        });
    };

    const allEffectsSelected =
        linkableEffects.length > 0 && pickedEffects.size === linkableEffects.length;

    const toggleSelectAllEffects = () => {
        setPickedEffects((prev) =>
            prev.size === linkableEffects.length
                ? new Set()
                : new Set(linkableEffects.map((e) => e.ID))
        );
    };

    // Link every checked effect. Both MSI and own effects go through the same
    // "whole effect" path (subscribeToEffect resolves MSI indicators itself and
    // takes own-effect questions as an argument). We link sequentially and
    // advance the Position offsets ourselves — passing the same base length to
    // every call would collide all new effects/syncs on one Position.
    const handleSubscribeSelectedEffects = async () => {
        if (busy || pickedEffects.size === 0) return;
        setBusy(true);
        setError('');

        const effectsToLink = linkableEffects.filter((e) => pickedEffects.has(e.ID));
        let effectsOffset = (projectEffects || []).length;
        let syncsOffset = (projectSyncs || []).length;
        const added = [];

        // Each subscribeToEffect is its own batch, so a mid-loop failure leaves
        // the earlier effects committed. Reset `busy` in `finally` so a thrown
        // error stays visible (we don't swallow it) without freezing the modal.
        try {
            for (const effect of effectsToLink) {
                const isMSI = effect.Standard === 'true';
                // Own effects need their questions fetched here (the drill-down
                // hook only runs for the effect being drilled into); MSI effects
                // resolve their standardised indicators inside subscribeToEffect.
                const portfolioQuestions = isMSI
                    ? []
                    : await fetchPortfolioQuestions(effect.ID, selectedPortfolioId);

                const result = await subscribeToEffect({
                    portfolioEffect: effect,
                    portfolioQuestions,
                    parentId: selectedPortfolioId,
                    effectsLength: effectsOffset,
                    syncsLength: syncsOffset,
                });

                added.push(result);
                effectsOffset += 1;
                // subscribeToEffect writes one Effect/MSIEffect sync per effect,
                // plus one Question sync per non-MSI question (MSI indicators ride
                // the effect's SyncId and get no rows of their own).
                syncsOffset += isMSI ? 1 : 1 + portfolioQuestions.length;
            }
        } catch (err) {
            // Earlier effects in the loop are already committed; the modal stays
            // open so the list shows them as linked next to the message.
            setError(err.message);
            return;
        } finally {
            setBusy(false);
        }

        if (typeof onSubscribed === 'function') onSubscribed(added);
        closeModal();
    };

    const handleSubscribeSelectedQuestions = async () => {
        if (busy || !drilldownEffect) return;
        if (pickedQuestions.size === 0 || !targetEffectId) return;

        setBusy(true);
        const selectedQuestionDocs = drilldownPortfolioQuestions.filter((q) => pickedQuestions.has(q.ID));

        await subscribeToQuestions({
            portfolioQuestions: selectedQuestionDocs,
            targetEffectId,
            parentId: selectedPortfolioId,
            fieldsLength: 0,
            syncsLength: (projectSyncs || []).length,
        });

        closeModal();
    };

    const enterDrilldown = (effect) => {
        setDrilldownEffectId(effect.ID);
        setPickedQuestions(new Set());
        setTargetEffectId('');
        setStep('questions');
    };

    const togglePickedQuestion = (questionId) => {
        setPickedQuestions((prev) => {
            const next = new Set(prev);
            if (next.has(questionId)) next.delete(questionId);
            else next.add(questionId);
            return next;
        });
    };

    // --- Renderers ---------------------------------------------------------

    const renderListStep = () => {
        if (!selectedPortfolioId) {
            return <p className="subscribe-modal__empty">Geen portfolio geselecteerd.</p>;
        }

        return (
            <>
                {portfolioEffectsList.length === 0 ? (
                    <p className="subscribe-modal__empty">Geen koppelbare effecten gevonden in deze portfolio.</p>
                ) : (
                    <>
                        <label className="subscribe-modal__checkbox-row subscribe-modal__select-all">
                            <input
                                type="checkbox"
                                checked={allEffectsSelected}
                                onChange={toggleSelectAllEffects}
                                disabled={busy || linkableEffects.length === 0}
                            />
                            <span>Selecteer alles</span>
                        </label>

                        <div className="subscribe-modal__list">
                            {portfolioEffectsList.map((effect) => {
                                const isMSI = effect.Standard === 'true';
                                const isLinked = linkedEffectIds.has(effect.ID);
                                return (
                                    <div key={effect.ID} className="subscribe-modal__item">
                                        <label className="subscribe-modal__checkbox-row">
                                            <input
                                                type="checkbox"
                                                checked={isLinked || pickedEffects.has(effect.ID)}
                                                onChange={() => togglePickedEffect(effect.ID)}
                                                disabled={busy || !syncsLoaded || isLinked}
                                            />
                                            <p className="subscribe-modal__item-title">
                                                {effect.Effect || '(zonder titel)'}
                                                {isMSI && (
                                                    <a
                                                        href="https://meetstandaard.nl"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="subscribe-modal__msi-badge"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        Meetstandaard.nl
                                                    </a>
                                                )}
                                                {isLinked && (
                                                    <span className="subscribe-modal__linked-badge">Al gekoppeld</span>
                                                )}
                                            </p>
                                        </label>
                                        {!isMSI && (
                                            <div className="subscribe-modal__item-actions">
                                                <button
                                                    className="subscribe-modal__btn"
                                                    disabled={busy}
                                                    onClick={() => enterDrilldown(effect)}
                                                >
                                                    Specifieke vragen kiezen ▶
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                        })}
                        </div>

                        {error && <p className="subscribe-modal__error">{error}</p>}

                        <div className="subscribe-modal__footer">
                            <button
                                className="subscribe-modal__btn subscribe-modal__btn--primary"
                                disabled={busy || pickedEffects.size === 0}
                                onClick={handleSubscribeSelectedEffects}
                            >
                                {pickedEffects.size > 0
                                    ? `Koppel geselecteerde effecten (${pickedEffects.size})`
                                    : 'Koppel geselecteerde effecten'}
                            </button>
                        </div>
                    </>
                )}
            </>
        );
    };

    const renderQuestionsStep = () => {
        if (!drilldownEffect) return null;

        return (
            <div className="subscribe-modal__drilldown">
                <button
                    type="button"
                    className="subscribe-modal__back"
                    onClick={resetDrilldown}
                >
                    ← Terug naar effecten
                </button>

                <div className="subscribe-modal__drilldown-context">
                    Vragen kiezen uit effect: <b>{drilldownEffect.Effect || '(zonder titel)'}</b>
                </div>

                {drilldownPortfolioQuestions.length === 0 ? (
                    <p className="subscribe-modal__empty">Geen koppelbare vragen gevonden bij dit effect.</p>
                ) : (
                    <>
                        <div className="subscribe-modal__field">
                            <label className="subscribe-modal__field-label">Selecteer vragen</label>
                            <div className="subscribe-modal__checkbox-list">
                                {drilldownPortfolioQuestions.map((q) => (
                                    <label key={q.ID} className="subscribe-modal__checkbox-row">
                                        <input
                                            type="checkbox"
                                            checked={pickedQuestions.has(q.ID)}
                                            onChange={() => togglePickedQuestion(q.ID)}
                                        />
                                        <span>{q.Question || '(zonder titel)'}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div className="subscribe-modal__field">
                            <label className="subscribe-modal__field-label">Vraag koppelen aan eigen effect</label>
                            {targetableProjectEffects.length === 0 ? (
                                <p className="subscribe-modal__empty">
                                    Geen eigen effecten gevonden — maak er eerst eentje aan via 'Eigen effect toevoegen'. (MSI-effecten kunnen geen doel zijn.)
                                </p>
                            ) : (
                                <select
                                    className="subscribe-modal__select"
                                    value={targetEffectId}
                                    onChange={(e) => setTargetEffectId(e.target.value)}
                                >
                                    <option value="">-- Kies een eigen effect --</option>
                                    {targetableProjectEffects.map((pe) => (
                                        <option key={pe.ID} value={pe.ID}>
                                            {pe.Effect || '(zonder titel)'}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>

                        <div className="subscribe-modal__footer">
                            <button
                                className="subscribe-modal__btn subscribe-modal__btn--primary"
                                disabled={busy || pickedQuestions.size === 0 || !targetEffectId}
                                onClick={handleSubscribeSelectedQuestions}
                            >
                                Geselecteerde vragen koppelen
                            </button>
                        </div>
                    </>
                )}
            </div>
        );
    };

    return (
        <CommonModal
            modalOpen={modalOpen}
            setModalOpen={(value) => { if (!value) closeModal(); else setModalOpen(true); }}
            pageHeader={
                step === 'questions'
                    ? `Vragen koppelen — ${drilldownEffect?.Effect || ''}`
                    : selectedPortfolioId
                        ? `Effecten uit ${portfolioName} koppelen`
                        : 'Effecten uit portfolio koppelen'
            }
            hideConfirm={true}
            content={
                <div className="subscribe-modal">
                    {step === 'list' ? renderListStep() : renderQuestionsStep()}
                </div>
            }
        />
    );
};

export default SubscribePortfolioEffectsModal;
